import { useState } from "react";
import { InfoTooltip as InfoIcon } from "./InfoTooltip";

interface AllocSegment {
  label: string;
  pct: number;
  color: string;
}

interface AllocBreakdown {
  credit_quality?: AllocSegment[];
  maturity?: AllocSegment[];
  industry?: AllocSegment[];
  geography?: AllocSegment[];
  as_of_date?: string | null;
  weighted_avg_maturity?: string | null;
  footnotes?: {
    credit?: string | null;
    maturity?: string | null;
    industry?: string | null;
    geography?: string | null;
  } | null;
}

type TabKey = "credit_quality" | "maturity" | "industry" | "geography";

const TAB_LABELS: Record<TabKey, string> = {
  credit_quality: "Credit Quality",
  maturity: "Maturity",
  industry: "Industry",
  geography: "Geography",
};

function AllocTabView({
  tab,
  segments,
  stat,
  footnote,
}: {
  tab: TabKey;
  segments: AllocSegment[];
  stat: string;
  footnote?: string | null;
}) {
  const labelMap: Record<TabKey, string> = {
    credit_quality: "Credit Quality",
    maturity: "Maturity Buckets",
    industry: "Industry Allocation",
    geography: "Geographic Allocation",
  };

  const superscriptMap: Record<TabKey, string> = {
    credit_quality: "¹",
    maturity: "²",
    industry: "³",
    geography: "⁴",
  };

  const headingTipMap: Record<TabKey, string> = {
    credit_quality:
      "Credit quality shows the mix of credit ratings across the fund's bonds, reflecting the average ability of issuers to repay their debt.",
    maturity:
      "Shows how the fund's bonds are spread across different ranges of time remaining until they come due.",
    industry: "Shows how the fund's corporate bonds are divided across different business sectors.",
    geography: "Shows the breakdown of the fund's bonds by the country where each issuer is based.",
  };

  const defaultFootnoteMap: Record<TabKey, string> = {
    credit_quality:
      "Credit quality reflects the average of ratings assigned by Standard & Poor's, Moody's, and Fitch (if all are available). U.S. Treasury obligations are shown with their assigned letter rating where available and as an em-dash (—) where no rating is published. Credit ratings are subject to change.",
    maturity:
      "Maturity buckets reflect each holding's remaining time to maturity as of the date shown. The portfolio's effective duration may differ from its weighted average maturity due to coupon structure and callability.",
    industry:
      "Industry allocations reflect issuer-level industry classifications and may not sum to 100% due to rounding and cash positions.",
    geography:
      "Geographic allocations reflect the country of domicile of the issuing entity. All holdings are U.S. dollar-denominated regardless of issuer domicile.",
  };

  const sup = superscriptMap[tab];
  const headingTip = headingTipMap[tab];
  const effectiveFootnote = footnote ?? defaultFootnoteMap[tab];

  return (
    <div className="px-10 max-md:px-4 py-8 max-md:py-6">
      <div className="flex items-center justify-between mb-5">
        <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white2)] tracking-[0.04em] inline-flex items-center gap-0.5">
          {labelMap[tab]}
          <sup className="text-[0.7em] -top-[0.4em]">{sup}</sup>
          <InfoIcon tip={headingTip} direction="right" />
        </span>
        <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tracking-[0.04em]">
          {stat}
        </span>
      </div>

      <div className="grid grid-cols-2 min-[480px]:grid-cols-3 gap-x-4 gap-y-2 mb-6">
        {segments.map((seg) => (
          <div key={seg.label} className="flex items-center gap-2 min-w-0">
            <span
              className="shrink-0 w-2.5 h-2.5 rounded-[2px]"
              style={{
                backgroundColor: seg.color,
                boxShadow: `0 0 4px ${seg.color}4D`,
              }}
            />
            <span className="font-[family-name:var(--font-instrument)] text-[12px] text-[var(--etf-white3)] tracking-[0.04em] truncate flex-1 whitespace-nowrap">
              {seg.label}
            </span>
            <span className="font-[family-name:var(--font-instrument)] text-[12px] font-semibold text-[var(--etf-white)] tracking-[0.02em] shrink-0">
              {seg.pct.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>

      <div className="relative">
        <div className="flex h-7 rounded-[4px] overflow-hidden bg-white/[0.02]">
          {segments.map((seg) => (
            <div
              key={seg.label}
              className="relative group h-full overflow-hidden"
              style={{ flex: seg.pct }}
              title={`${seg.label} · ${seg.pct.toFixed(2)}%`}
            >
              <div
                className="absolute inset-0"
                style={{
                  background: `repeating-linear-gradient(90deg, ${seg.color} 0px, ${seg.color} 2px, transparent 2px, transparent 4.5px)`,
                  opacity: 0.9,
                }}
              />
              <div
                className="absolute inset-0"
                style={{
                  background: "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 60%)",
                }}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-150 whitespace-nowrap">
                <div className="bg-[#1a1a1a] border border-[var(--etf-border)] rounded px-2.5 py-1.5 font-[family-name:var(--font-instrument)] text-[10.5px] text-[var(--etf-white2)] shadow-lg">
                  {seg.label} · {seg.pct.toFixed(2)}%
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="relative h-5 mt-1">
          {[0, 25, 50, 75, 100].map((tick) => (
            <div
              key={tick}
              className="absolute top-0 flex flex-col items-center"
              style={{
                left: `${tick}%`,
                transform:
                  tick === 100 ? "translateX(-100%)" : tick === 0 ? "none" : "translateX(-50%)",
              }}
            >
              <div className="w-px h-1.5 bg-white/[0.12]" />
              <span className="font-[family-name:var(--font-instrument)] text-[9px] text-[var(--etf-white3)] tracking-[0.04em] leading-none mt-0.5">
                {tick}%
              </span>
            </div>
          ))}
        </div>
      </div>

      {effectiveFootnote && (
        <p className="font-[family-name:var(--font-instrument)] text-[10px] text-[var(--etf-white3)] tracking-[0.04em] mt-3 leading-relaxed">
          <sup className="text-[0.85em]">{sup}</sup> {effectiveFootnote}
        </p>
      )}
    </div>
  );
}

export function FundAllocationBreakdown({
  breakdown,
  emptyTitle = "Allocation",
  emptyContext = "default",
  asOfFallback,
  maturityFallback,
}: {
  breakdown: AllocBreakdown | null | undefined;
  emptyTitle?: string;
  emptyContext?: "default" | "bond";
  asOfFallback?: string;
  maturityFallback?: string;
}) {
  const availableTabs: TabKey[] = (
    ["credit_quality", "maturity", "industry", "geography"] as TabKey[]
  ).filter((k) => (breakdown?.[k]?.length ?? 0) > 0);

  const [activeTab, setActiveTab] = useState<TabKey>(availableTabs[0] ?? "credit_quality");

  const isEmpty = !breakdown || availableTabs.length === 0;

  const activeSections = isEmpty ? null : breakdown;

  const getTabStat = (tab: TabKey): string => {
    if (!activeSections) return "";
    const segs = activeSections[tab] ?? [];
    if (tab === "credit_quality")
      return `${segs.length} rating tier${segs.length !== 1 ? "s" : ""}`;
    if (tab === "maturity") {
      const wam = activeSections.weighted_avg_maturity;
      return wam
        ? `Weighted Avg Maturity · ${wam}`
        : `${segs.length} bucket${segs.length !== 1 ? "s" : ""}`;
    }
    if (tab === "industry") return `${segs.length} sector${segs.length !== 1 ? "s" : ""}`;
    return `${segs.length} region${segs.length !== 1 ? "s" : ""}`;
  };

  const getTabSubtitle = (tab: TabKey): string => {
    if (!activeSections) return "";
    const asOf = activeSections.as_of_date ?? asOfFallback ?? null;
    const maturityCtx = activeSections.weighted_avg_maturity ?? maturityFallback ?? null;
    if (tab === "credit_quality")
      return `Breakdown by issuer credit rating${asOf ? ` · As of ${asOf}` : ""}`;
    if (tab === "maturity")
      return `Breakdown by remaining years to maturity${maturityCtx ? ` · ${maturityCtx}` : ""}`;
    if (tab === "industry") return `Corporate sector exposure${asOf ? ` · As of ${asOf}` : ""}`;
    return `Issuer country of domicile${asOf ? ` · As of ${asOf}` : ""}`;
  };

  const titleMap: Record<TabKey, string> = {
    credit_quality: "Credit Quality",
    maturity: "Maturity Distribution",
    industry: "Industry Breakdown",
    geography: "Geographic Breakdown",
  };

  const sectionTitle = isEmpty ? emptyTitle : titleMap[activeTab];
  const sectionSubtitle = isEmpty
    ? emptyContext === "bond"
      ? "Breakdown by issuer credit rating · Pending fund launch"
      : null
    : getTabSubtitle(activeTab);

  return (
    <section className="border-b border-[var(--etf-border)]" id="sec-allocation">
      <div className="flex items-start justify-between px-10 max-md:px-4 py-6 max-md:py-4 border-b border-[var(--etf-border)] gap-4 flex-wrap">
        <div>
          <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] max-[480px]:text-[20px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3 mb-1">
            <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)] shrink-0" />
            {sectionTitle}
          </h2>
          {sectionSubtitle && (
            <p className="font-[family-name:var(--font-geist)] text-[12px] tracking-[0.08em] text-[var(--etf-white3)] hidden min-[600px]:block pl-6 mt-1">
              {sectionSubtitle}
            </p>
          )}
        </div>

        {!isEmpty && availableTabs.length > 1 && (
          <div className="flex items-center gap-0 flex-wrap border border-[var(--etf-border)] rounded-[2px] overflow-hidden">
            {availableTabs.map((tab, i) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={[
                  "font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase px-4 py-2 whitespace-nowrap cursor-pointer transition-colors duration-150",
                  i < availableTabs.length - 1 ? "border-r border-[var(--etf-border)]" : "",
                  activeTab === tab
                    ? "bg-[var(--etf-orange)] text-white"
                    : "bg-transparent text-[var(--etf-white3)] hover:bg-white/[0.04] hover:text-[var(--etf-white)]",
                ].join(" ")}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        )}
      </div>

      {isEmpty ? (
        <div className="px-10 max-md:px-4 py-10 max-md:py-7">
          <p className="font-[family-name:var(--font-f37bolton)] text-[18px] text-[var(--etf-white)] leading-snug tracking-[-0.01em] mb-3">
            {emptyContext === "bond"
              ? "Credit quality breakdown not yet available."
              : "Allocation data is not yet available."}
          </p>
          <p className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white3)] leading-[1.7] m-0 max-w-[640px]">
            {emptyContext === "bond"
              ? "Once the Fund commences operations and publishes initial holdings, this section will show the portfolio's credit-rating mix and maturity distribution per the underlying index."
              : "Credit quality, maturity distribution, and sector breakdown will appear here once the Fund has commenced operations and published initial holdings."}
          </p>
        </div>
      ) : (
        <AllocTabView
          tab={activeTab}
          segments={activeSections?.[activeTab] ?? []}
          stat={getTabStat(activeTab)}
          footnote={
            activeSections?.footnotes?.[
              activeTab === "credit_quality"
                ? "credit"
                : activeTab === "maturity"
                  ? "maturity"
                  : activeTab === "industry"
                    ? "industry"
                    : "geography"
            ]
          }
        />
      )}
    </section>
  );
}
